import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Lock, Mail, Plus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { StatusGroup } from "@/components/status/status-button";
import { STATUS_SHAPES, STATUS_SHORT_LABELS, type StatusKey } from "@/lib/status";
import {
  ApiError,
  addEntryRevision,
  getEntry,
  type EntryDetail as EntryDetailData,
} from "@/lib/api";
import { entryWhen, timeOfDay } from "@/lib/format";

const MOOD_LABELS: Record<StatusKey, string> = {
  green: "I'm doing well",
  yellow: "Something feels off",
  red: "I need help",
};

function StatusBadge({ status, label }: { status: StatusKey; label?: string }) {
  const Shape = STATUS_SHAPES[status];
  return (
    <Badge status={status}>
      <Shape className="h-[11px] w-[11px]" strokeWidth={2} />
      {label ?? STATUS_SHORT_LABELS[status]}
    </Badge>
  );
}

export default function EntryDetail() {
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const [entry, setEntry] = useState<EntryDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add-revision form state.
  const [revItemId, setRevItemId] = useState<string>("");
  const [revStatus, setRevStatus] = useState<StatusKey | undefined>();
  const [revNote, setRevNote] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  async function load() {
    try {
      setEntry(await getEntry(id));
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) navigate("/signin");
      else if (e instanceof ApiError && e.status === 404) setError("Entry not found.");
      else setError("Couldn't load this entry.");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function saveRevision() {
    if (!revItemId || !revStatus) return;
    setSaving(true);
    try {
      await addEntryRevision(id, {
        entry_item_id: revItemId,
        new_status: revStatus,
        notes: revNote.trim() || null,
      });
      setShowForm(false);
      setRevItemId("");
      setRevStatus(undefined);
      setRevNote("");
      await load();
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) navigate("/signin");
      else setError("Couldn't save your revision. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center gap-2 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" strokeWidth={2} />
        <span className="text-sm">Loading…</span>
      </div>
    );
  }
  if (error || !entry) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 px-6 text-center">
        <p className="text-sm text-muted-foreground">{error ?? "Not available."}</p>
        <Button variant="outline" onClick={() => navigate("/app")}>
          Return to Dashboard
        </Button>
      </div>
    );
  }

  const revisableItems = entry.items.filter((i) => !i.skipped);
  const chosenItem = entry.items.find((i) => i.id === revItemId);

  return (
    <div className="pb-16">
      <div className="flex items-center gap-2 px-4 pb-2 pt-6">
        <Button variant="ghost" size="icon" aria-label="Back" onClick={() => navigate("/app")}>
          <ArrowLeft className="h-5 w-5" strokeWidth={2} />
        </Button>
        <h1 className="flex-1 text-lg font-semibold text-foreground">
          {entryWhen(entry.created_at)}
        </h1>
        {entry.overall_status && <StatusBadge status={entry.overall_status} />}
      </div>

      <div className="flex flex-col gap-3 px-6 pt-2">
        {entry.mood_status && (
          <Card>
            <CardContent className="flex items-center justify-between gap-3 px-4 py-3">
              <div>
                <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
                  Mood
                </p>
                {entry.mood_note && (
                  <p className="mt-0.5 text-[13px] text-muted-foreground">{entry.mood_note}</p>
                )}
              </div>
              <StatusBadge status={entry.mood_status} label={MOOD_LABELS[entry.mood_status]} />
            </CardContent>
          </Card>
        )}

        {entry.items.length > 0 && (
          <Card>
            <CardContent className="flex flex-col px-4 py-1">
              {entry.items.map((it, i) => {
                const answered = it.status && !it.skipped;
                return (
                  <div
                    key={it.id}
                    className={`flex items-center justify-between gap-3 py-2.5 ${
                      i < entry.items.length - 1 ? "border-b border-border" : ""
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-foreground">{it.question_text}</p>
                      {it.notes && (
                        <p className="mt-0.5 text-xs text-muted-foreground">{it.notes}</p>
                      )}
                    </div>
                    {answered ? (
                      <StatusBadge status={it.status as StatusKey} />
                    ) : (
                      <span className="text-xs text-muted-foreground">Skipped</span>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {entry.journal_text && (
          <Card>
            <CardContent className="px-4 py-3">
              <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
                Journal
              </p>
              <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-foreground">
                {entry.journal_text}
              </p>
            </CardContent>
          </Card>
        )}

        <div className="mt-1 flex items-center gap-2">
          <Lock className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={2} />
          <p className="text-xs text-muted-foreground">
            Original entry is locked · Saved {timeOfDay(entry.created_at)}
          </p>
        </div>

        <p className="mt-2 text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
          Revisions
        </p>
        {entry.revisions.length === 0 && !showForm && (
          <p className="text-sm text-muted-foreground">
            No revisions yet. If something shifts, you can add one below.
          </p>
        )}
        {entry.revisions.map((r) => (
          <Card
            key={r.id}
            className={r.new_status === "red" ? "border-2 border-status-red" : ""}
          >
            <CardContent className="flex flex-col gap-2 px-4 py-3">
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-muted-foreground">
                  {r.question_text} · {timeOfDay(r.created_at)}
                </p>
                <StatusBadge status={r.new_status} />
              </div>
              {r.notes && (
                <p className="text-sm leading-relaxed text-foreground">{r.notes}</p>
              )}
              {r.new_status === "red" && (
                <div className="flex items-start gap-2 border-t border-border pt-2">
                  <Mail className="mt-0.5 h-3.5 w-3.5 shrink-0 text-status-red-text" strokeWidth={2} />
                  <p className="text-xs leading-relaxed text-muted-foreground">
                    Your Sponsor and Trusted Contacts were notified.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        ))}

        {showForm ? (
          <Card>
            <CardContent className="flex flex-col gap-3 p-4">
              <div>
                <p className="mb-1.5 text-sm font-medium text-foreground">
                  Which answer are you revising?
                </p>
                <div className="flex flex-col gap-1.5">
                  {revisableItems.map((it) => (
                    <button
                      key={it.id}
                      type="button"
                      onClick={() => setRevItemId(it.id)}
                      className={`rounded-md border px-3 py-2 text-left text-sm transition-colors ${
                        revItemId === it.id
                          ? "border-primary bg-primary/[0.06] text-foreground"
                          : "border-border text-muted-foreground"
                      }`}
                    >
                      {it.question_text}
                    </button>
                  ))}
                </div>
              </div>
              {chosenItem && (
                <div>
                  <p className="mb-1.5 text-sm font-medium text-foreground">Where is it now?</p>
                  <StatusGroup value={revStatus} onChange={setRevStatus} />
                </div>
              )}
              <Textarea
                rows={3}
                placeholder="What changed? (optional)"
                value={revNote}
                onChange={(e) => setRevNote(e.target.value)}
              />
              <div className="flex gap-2">
                <Button disabled={!revItemId || !revStatus || saving} onClick={saveRevision}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} /> : "Save revision"}
                </Button>
                <Button variant="ghost" onClick={() => setShowForm(false)} disabled={saving}>
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Button variant="outline" fullWidth onClick={() => setShowForm(true)}>
            <Plus className="h-4 w-4" strokeWidth={2} />
            Add Revision
          </Button>
        )}
        <p className="text-center text-xs text-muted-foreground">
          Revisions are appended — the original stays as written.
        </p>
      </div>
    </div>
  );
}
